/**
 * A custom React hook that provides flexible sorting functionality for arrays of items.
 *
 * This hook combines predefined sorting options with dynamic attribute-based sorting:
 *
 * 1. Predefined Sort Options:
 *    - LAST_MODIFIED: Returns items in their original order
 *    - CREATION_DATE_ASC: Sorts items by creation timestamp in ascending order
 *    - CREATION_DATE_DESC: Sorts items by creation timestamp in descending order
 *
 * 2. Dynamic Attribute Sorting:
 *    - Automatically detects if the sortBy parameter matches any attribute name in the items
 *    - Supports automatic type detection and appropriate sorting for:
 *      * String attributes: Sorts alphabetically using localeCompare
 *      * Number attributes: Sorts numerically in ascending order
 *
 * Key Features:
 * - Type-safe: Uses TypeScript generics to ensure type safety
 * - Null-safe: Handles undefined items and attributes gracefully
 * - Warning system: Provides meaningful console warnings for common issues
 * - Immutable: Never mutates the original array
 * - Memoized: Uses useMemo to prevent unnecessary re-sorting
 *
 * Common Use Cases:
 * 1. Default timestamp-based sorting:
 *    ?sortBy=CREATION_DATE_ASC
 * 2. Dynamic attribute sorting:
 *    ?sortBy=name (for alphabetical sorting)
 *    ?sortBy=size (for numerical sorting)
 *
 * Error Handling:
 * - Returns original array if sortBy parameter is missing or invalid
 * - Warns if creation timestamps are missing when using date sorting
 * - Warns if specified attribute doesn't exist in items
 * - Warns if attribute type is not supported (only string and number are supported)
 *
 * Performance Considerations:
 * - Sorting is memoized based on items and sortBy parameter
 * - Type checking is performed only on the first item to minimize overhead
 * - Uses native array sort with optimal comparator functions
 *
 * @template T - Type of items to be sorted, must extend Partial<TAsset>
 * @param {T[] | undefined} items - Array of items to be sorted
 * @returns {T[]} - Sorted array of items
 *
 * @example
 * // Basic usage
 * const MyComponent = () => {
 *   const items = [{ name: 'B' }, { name: 'A' }];
 *   const sortedItems = useApplySortFilter(items);
 *   return (
 *     <div>
 *       {sortedItems.map(item => <div>{item.name}</div>)}
 *     </div>
 *   );
 * };
 *
 * @example
 * // With different sort parameters in URL
 * // /page?sortBy=CREATION_DATE_ASC - Sorts by creation date ascending
 * // /page?sortBy=name - Sorts alphabetically by name
 * // /page?sortBy=size - Sorts numerically by size
 */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { TDataItem } from '@/shared/components/ui/data-table/context/data-table-context.types';
import { SORT_BY_QUERY_PARAM_NAME } from '@/shared/components/ui/data-table/hooks/use-filter-params/use-filter-params.constants';
import {
  DefaultSortOptions,
  TSortByProps,
} from '@/shared/components/ui/sort-by/sort-by.types';

export const useApplySortFilter = <T extends Partial<TDataItem>>(
  items: T[] | undefined,
  sortingOptions?: Omit<TSortByProps<T>, 'isLoading' | 'additionalOptions'>,
) => {
  const [searchParams] = useSearchParams();
  const queryParam = sortingOptions?.queryParam ?? SORT_BY_QUERY_PARAM_NAME;
  const sortBy = searchParams.get(queryParam)
    ? searchParams.get(queryParam)
    : sortingOptions?.defaultOption;

  const sortedItems = useMemo(() => {
    if (!items) return [];
    if (!sortBy) return items;

    // Handle default sorting options
    if (sortBy === DefaultSortOptions.LAST_MODIFIED) {
      return items;
    }

    if (
      sortBy === DefaultSortOptions.CREATION_DATE_ASC ||
      sortBy === DefaultSortOptions.CREATION_DATE_DESC
    ) {
      // Check if all items have creationTimestamp before sorting
      const hasCreationDates = items.every((item) => item.creationTimestamp);
      if (!hasCreationDates) {
        console.warn('Some items are missing creationTimestamp');
        return items;
      }

      return [...items].sort((a, b) => {
        const dateA = new Date(a.creationTimestamp!);
        const dateB = new Date(b.creationTimestamp!);

        return sortBy === DefaultSortOptions.CREATION_DATE_ASC
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      });
    }

    // Handle dynamic attribute sorting
    const firstItem = items[0];
    if (!firstItem || !(sortBy in firstItem)) {
      console.warn(`Attribute "${sortBy.toString()}" not found in items`);
      return items;
    }

    const sampleValue = firstItem[sortBy as keyof T];
    const valueType = typeof sampleValue;

    if (valueType !== 'string' && valueType !== 'number') {
      console.warn(
        `Attribute "${sortBy.toString()}" is of type "${valueType}", only string and number types are supported for sorting`,
      );
      return items;
    }

    return [...items].sort((a, b) => {
      const valueA = a[sortBy as keyof T];
      const valueB = b[sortBy as keyof T];

      // Handle potential undefined values
      if (valueA === undefined && valueB === undefined) return 0;
      if (valueA === undefined) return 1;
      if (valueB === undefined) return -1;

      if (valueType === 'string') {
        return (valueA as string).localeCompare(valueB as string);
      }

      return (valueA as number) - (valueB as number);
    });
  }, [items, sortBy]);

  return sortedItems;
};
